import { EmptyState, FlexBox } from '@dixa/lib-shared-habla';
import { CollectionManagementNav } from 'collection/pages/collectionEdit/components/layout/layout';
import Page from 'lib-knowledge-utils/components/page/index';

import { ResizablePane } from 'components/resizablePane';
import { useSuggestionsListWidth } from 'hooks/useSuggestionsListWidth';

import s from './articleSuggestions.module.scss';
import { SelectedSuggestionPreview } from './components/selectedSuggestionPreview';
import { SuggestionsList } from './components/suggestionsList';
import { useAiDraftLiveUpdates } from './hooks/useAiDraftLiveUpdates';
import { useSelectedSuggestion } from './hooks/useSelectedSuggestion';

const listDefaultWidth = 332;
const listMinWidth = 280;
const previewMinWidth = 480;

/**
 * Review queue for the article drafts AI writes from conversations.
 *
 * Holds the selected draft's id only, in the URL, so a draft can be linked to.
 * The preview follows the draft itself in the cache, so revising or re-filing
 * it from there shows up without the page having to pass a fresh copy down.
 *
 * A draft that is approved or discarded leaves the queue, so the selection is
 * dropped with it — also when the AI or another reviewer is the one who took
 * it. Their changes reach the queue over the host app's web socket, see
 * `useAiDraftLiveUpdates`.
 */
export function ArticleSuggestionsPage(): JSX.Element {
  const { selectedId, select, clearSelection } = useSelectedSuggestion();
  const [listWidth, setListWidth] = useSuggestionsListWidth();
  // A draft that left the queue elsewhere takes the selection with it, as one
  // the reviewer approves or discards here does.
  useAiDraftLiveUpdates({
    onGone: (id) => {
      if (id === selectedId) clearSelection();
    },
  });

  return (
    <Page>
      <Page.Body flexDirection="row">
        <CollectionManagementNav />
        <FlexBox
          flexDirection="row"
          width="100%"
          height="100%"
          minHeight="0"
          className={s.panes}
        >
          <ResizablePane
            width={listWidth}
            defaultWidth={listDefaultWidth}
            minWidth={listMinWidth}
            minRemainingWidth={previewMinWidth}
            onWidthChange={setListWidth}
            label="Resize suggestions list"
          >
            <SuggestionsList
              selectedId={selectedId}
              onSelect={(suggestion) => select(suggestion.id)}
            />
          </ResizablePane>
          {selectedId ? (
            // Keyed so switching drafts remounts rather than leaving the body
            // scrolled to where the previous one was read to.
            <SelectedSuggestionPreview
              key={selectedId}
              suggestionId={selectedId}
              onRemoved={clearSelection}
            />
          ) : (
            <FlexBox
              flexDirection="column"
              justifyContent="center"
              flex="1"
              height="100%"
              background="neutral.000"
            >
              <EmptyState
                size="small"
                icon="ai"
                title="Select a suggestion"
                description="Pick a suggestion from the list to preview it."
              />
            </FlexBox>
          )}
        </FlexBox>
      </Page.Body>
    </Page>
  );
}

export default ArticleSuggestionsPage;
